import type { ReactNode } from 'react';
import type { QueryError } from '@/types/api';

export function Spinner({ label }: { label: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-2 text-muted" role="status">
      <span
        aria-hidden="true"
        className="inline-block h-3 w-3 animate-spin rounded-full border border-line-strong border-t-local"
      />
      <span className="text-xs">{label}</span>
    </span>
  );
}

export function LoadingState({ label }: { label: string }): JSX.Element {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <Spinner label={label} />
    </div>
  );
}

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
      <p className="text-sm text-ink">{title}</p>
      {hint ? <p className="max-w-xs text-xs text-muted">{hint}</p> : null}
      {action}
    </div>
  );
}

/**
 * Renders a failed query. A query failure is never allowed to look like an
 * empty result -- spec §6, and the CustomDataPool regression it cites.
 */
export function ErrorState({
  error,
  onRetry,
}: {
  error: QueryError;
  onRetry?: () => void;
}): JSX.Element {
  return (
    <div
      role="alert"
      className="m-3 overflow-hidden rounded-sm border border-danger/50 bg-danger-dim"
      data-testid="query-error"
    >
      <div className="flex items-center gap-2 border-b border-danger/30 px-3 py-1.5">
        <span className="eyebrow text-danger">Query failed</span>
        <code className="tnum rounded-sm border border-danger/40 px-1 text-[10px] text-danger">
          SQLSTATE {error.sqlState}
        </code>
        {typeof error.position === 'number' ? (
          <span className="tnum text-[10px] text-muted">at character {error.position + 1}</span>
        ) : null}
        {onRetry ? (
          <button type="button" className="btn ml-auto" onClick={onRetry}>
            Run again
          </button>
        ) : null}
      </div>
      <div className="px-3 py-2">
        <p className="font-mono text-xs leading-5 text-ink">{error.message}</p>
        {error.detail ? <p className="mt-1 text-xs text-muted">{error.detail}</p> : null}
        {error.hint ? (
          <p className="mt-1 text-xs text-muted">
            <span className="eyebrow mr-1.5 text-muted">Hint</span>
            {error.hint}
          </p>
        ) : null}
      </div>
    </div>
  );
}
